import React, { Suspense } from 'react'
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom'
// Date configuration is handled by shared/date-fns.ts with Thai locale support
import { Toaster } from '@/components/ui/sonner'
import AuthGuard from './components/auth/AuthGuard'
import CompanyGuard from './components/auth/CompanyGuard'
import Layout from './components/LayoutNew'
import Dashboard from './pages/DashboardNew'
import SignInPage from './pages/SignIn'
import SignUpPage from './pages/SignUp'
import LineCallbackPage from './pages/LineCallback'
import CompanySelection from './pages/CompanySelection'
import TokenRefreshIndicator from './components/auth/TokenRefreshIndicator'
import { ErrorBoundary } from './components/ErrorBoundary'

// Import auth test helpers for development
if (process.env.NODE_ENV === 'development') {
  import('./utils/authTestHelpers')
}

// Enhanced loading component with timeout handling and chunk loading error recovery
const PageLoader = () => {
	const [showTimeout, setShowTimeout] = React.useState(false)
	const [showError, setShowError] = React.useState(false)

	React.useEffect(() => {
		const timeoutTimer = setTimeout(() => {
			setShowTimeout(true)
		}, 10000) // Show timeout message after 10 seconds

		const errorTimer = setTimeout(() => {
			setShowError(true)
		}, 20000) // Show error message after 20 seconds

		return () => {
			clearTimeout(timeoutTimer)
			clearTimeout(errorTimer)
		}
	}, [])

	if (showError) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="text-center space-y-4 p-4">
					<div className="text-red-500 mb-4">
						<div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
							<span className="text-red-600 text-xl">⚠</span>
						</div>
					</div>
					<div>
						<h3 className="font-semibold text-gray-900 mb-2">Failed to load page</h3>
						<p className="text-gray-600 mb-4">There was an issue loading the page resources.</p>
						<button 
							onClick={() => window.location.reload()} 
							className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
						>
							Reload page
						</button>
					</div>
				</div>
			</div>
		)
	}

	if (showTimeout) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="text-center space-y-4 p-4">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
					<div>
						<p className="text-gray-600">Taking longer than usual to load...</p>
						<p className="text-sm text-gray-500 mt-1">Please wait or reload if needed</p>
						<button 
							onClick={() => window.location.reload()} 
							className="mt-2 text-blue-600 hover:text-blue-800 text-sm underline"
						>
							Reload page
						</button>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="flex items-center justify-center min-h-screen">
			<div className="text-center space-y-2">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
				<p className="text-sm text-gray-500">Loading page...</p>
			</div>
		</div>
	)
}

// Enhanced lazy loading with chunk loading error retry logic
const createLazyComponent = (importFn: () => Promise<any>, componentName: string) => {
	return React.lazy(() => 
		importFn().catch(error => {
			console.error(`Failed to load ${componentName}:`, error)
			// Return a fallback component that shows error and retry option
			return {
				default: () => (
					<div className="flex items-center justify-center min-h-screen">
						<div className="text-center space-y-4 p-6">
							<div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
								<span className="text-red-600 text-xl">⚠</span>
							</div>
							<h3 className="font-semibold text-gray-900 mb-2">Failed to load {componentName}</h3>
							<p className="text-gray-600 mb-4">There was an issue loading this page.</p>
							<button 
								onClick={() => window.location.reload()} 
								className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
							>
								Reload page
							</button>
						</div>
					</div>
				)
			}
		})
	)
}

// Lazy load heavy components with error handling
const Documents = createLazyComponent(() => import('./pages/DocumentsNew'), 'Documents')
const DocumentReview = createLazyComponent(() => import('./pages/DocumentReviewNew'), 'Document Review')
const Statistics = createLazyComponent(() => import('./pages/StatisticsNew'), 'Statistics')
const TaskDetail = createLazyComponent(() => import('./pages/TaskDetailNew'), 'Task Detail')

// LED Pages (lazy loaded)
const LEDDashboard = createLazyComponent(() => import('./pages/LED/LEDDashboard'), 'LED Dashboard')
const LEDCases = createLazyComponent(() => import('./pages/LED/LEDCases'), 'LED Cases')
const LEDCaseDetail = createLazyComponent(() => import('./pages/LED/LEDCaseDetail'), 'LED Case Detail')
const LEDNewCase = createLazyComponent(() => import('./pages/LED/LEDNewCase'), 'LED New Case')

// Litigation Pages (lazy loaded)
const LitigationDashboard = createLazyComponent(() => import('./pages/Litigation/LitigationDashboard'), 'Litigation Dashboard')
const LitigationCaseDetail = createLazyComponent(() => import('./pages/Litigation/LitigationCaseDetail'), 'Litigation Case Detail')

// Pay-in Slips Pages (lazy loaded)
const PayinSlipsDashboard = createLazyComponent(() => import('./pages/PayinSlips/PayinSlipsDashboard'), 'Pay-in Slips Dashboard')
const LEDPayinSlips = createLazyComponent(() => import('./pages/PayinSlips/LEDPayinSlips'), 'LED Pay-in Slips')
const LitigationPayinSlips = createLazyComponent(() => import('./pages/PayinSlips/LitigationPayinSlips'), 'Litigation Pay-in Slips')

// SLA Pages (lazy loaded - likely heaviest with charts)
const SLADashboard = createLazyComponent(() => import('./pages/SLA/SLADashboard'), 'SLA Dashboard')
const SLACases = createLazyComponent(() => import('./pages/SLA/SLACases'), 'SLA Cases')
const SLAAssignees = createLazyComponent(() => import('./pages/SLA/SLAAssignees'), 'SLA Assignees')
const SLACaseDetail = createLazyComponent(() => import('./pages/SLA/SLACaseDetail'), 'SLA Case Detail')
const SLAFlows = createLazyComponent(() => import('./pages/SLA/SLAFlows'), 'SLA Flows')
const SLATemplateEditorPage = createLazyComponent(() => import('./pages/SLA/SLATemplateEditorPage'), 'SLA Template Editor')
const SLAFlowDetail = createLazyComponent(() => import('./pages/SLA/SLAFlowDetail'), 'SLA Flow Detail')
const SLAFlowNew = createLazyComponent(() => import('./pages/SLA/SLAFlowNew'), 'SLA Flow New')
const SLAFlowDesignerNew = createLazyComponent(() => import('./pages/SLA/SLAFlowDesignerNew'), 'SLA Flow Designer New')
const SLAFlowDesignerEdit = createLazyComponent(() => import('./pages/SLA/SLAFlowDesignerEdit'), 'SLA Flow Designer Edit')
const SLAAdminMigration = createLazyComponent(() => import('./pages/SLA/SLAAdminMigration'), 'SLA Admin Migration')
const SLAReports = createLazyComponent(() => import('./pages/SLA/SLAReports'), 'SLA Reports')

// Admin Pages (lazy loaded)
const UserManagement = createLazyComponent(() => import('./pages/Admin/UserManagement'), 'User Management')
const InvitationManagement = createLazyComponent(() => import('./pages/Admin/InvitationManagement'), 'Invitation Management')

// Settings Pages (lazy loaded)
const UserProfile = createLazyComponent(() => import('./pages/Settings/UserProfile'), 'User Profile')

// Invitation Pages (lazy loaded)
const InvitationAcceptance = createLazyComponent(() => import('./pages/Invitation/InvitationAcceptance'), 'Invitation Acceptance')

// Unified Reports Pages (lazy loaded - heavy with charts)
const UnifiedReports = createLazyComponent(() => import('./pages/Reports/UnifiedReports'), 'Unified Reports')

// Thai locale is configured in shared/date-fns.ts

function App() {
	return (
		<Router>
			<Routes>
				{/* Public Auth Routes */}
				<Route path="/sign-in" element={<SignInPage />} />
				<Route path="/sign-up" element={<SignUpPage />} />
				<Route path="/auth/line/callback" element={<LineCallbackPage />} />
				
				{/* Public Invitation Routes */}
				<Route path="/invitation/accept/:module/:company" element={<InvitationAcceptance />} />
				
				{/* Company Selection Route (requires auth but not company) */}
				<Route 
					path="/select-company" 
					element={
						<ErrorBoundary>
							<AuthGuard requireCompany={false}>
								<CompanySelection />
							</AuthGuard>
						</ErrorBoundary>
					} 
				/>

				{/* Protected Routes (require auth + company selection) */}
				<Route
					path="/*"
					element={
						<ErrorBoundary>
							<AuthGuard>
								<CompanyGuard>
									<Layout>
										<ErrorBoundary
											onError={(error, errorInfo) => {
												console.error('Route Error Boundary caught:', error, errorInfo)
												// Handle chunk loading errors specifically
												if (error.message?.includes('Loading chunk') || error.message?.includes('ChunkLoadError')) {
													console.warn('Chunk loading error detected, reloading page')
													setTimeout(() => window.location.reload(), 1000)
												}
											}}
										>
											<Suspense fallback={<PageLoader />}>
											<Routes>
										<Route path="/" element={<Dashboard />} />
										<Route path="/documents" element={<Documents />} />
										<Route path="/statistics" element={<Statistics />} />
										<Route path="/task/:id" element={<TaskDetail />} />
										<Route
											path="/task/:taskId/document/:documentId"
											element={<DocumentReview />}
										/>
										
										{/* LED Routes */}
										<Route path="/led" element={<LEDDashboard />} />
										<Route path="/led/cases" element={<LEDCases />} />
										<Route path="/led/cases/new" element={<LEDNewCase />} />
										<Route path="/led/cases/:id" element={<LEDCaseDetail />} />
										
										{/* Litigation Routes */}
										<Route path="/litigation" element={<LitigationDashboard />} />
										<Route path="/litigation/cases/:id" element={<LitigationCaseDetail />} />
										
										{/* Pay-in Slips Routes */}
										<Route path="/payin-slips" element={<PayinSlipsDashboard />} />
										<Route path="/payin-slips/led/slips" element={<LEDPayinSlips />} />
										<Route path="/payin-slips/litigation/slips" element={<LitigationPayinSlips />} />
										
										{/* Reports Routes */}
										<Route path="/reports" element={<UnifiedReports />} />
										
										{/* SLA Routes */}
										<Route path="/sla" element={<SLADashboard />} />
										<Route path="/sla/cases" element={<SLACases />} />
										<Route path="/sla/cases/:id" element={<SLACaseDetail />} />
										<Route path="/sla/assignees" element={<SLAAssignees />} />
										<Route path="/sla/reports" element={<SLAReports />} />
										<Route path="/sla/flows" element={<SLAFlows />} />
										<Route path="/sla/flows/template/:templateCode" element={<SLATemplateEditorPage />} />
										<Route path="/sla/flows/detail/:templateCode" element={<SLAFlowDetail />} />
										<Route path="/sla/flows/new" element={<SLAFlowNew />} />
										<Route path="/sla/flows/designer/new" element={<SLAFlowDesignerNew />} />
										<Route path="/sla/flows/designer/:code" element={<SLAFlowDesignerEdit />} />
										<Route path="/sla/admin/migration" element={<SLAAdminMigration />} />
										
										{/* Admin Routes */}
										<Route path="/admin/users" element={<UserManagement />} />
										<Route path="/admin/invitations" element={<InvitationManagement />} />
										
										{/* Settings Routes */}
										<Route path="/settings/profile" element={<UserProfile />} />
											</Routes>
										</Suspense>
									</ErrorBoundary>
								</Layout>
							</CompanyGuard>
						</AuthGuard>
					</ErrorBoundary>
					}
				/>
			</Routes>
			<TokenRefreshIndicator />
			<Toaster richColors position="top-right" />
		</Router>
	)
}

export default App
